import React, { useState } from "react";
import { View } from "react-native";
import Animated, { FadeInUp } from "react-native-reanimated";

import Button from "$src/core/components/atoms/Button/Button";
import ProfileSelectorButton from "$src/core/components/molecules/ProfileSelectorButton/ProfileSelectorButton";
import { MAX_PROFILES } from "$src/core/constants/profile";
import { TEST_IDS } from "$src/core/constants/test-ids";
import useTranslation from "$src/core/hooks/use-translation";
import type { ProfileDto } from "$src/core/models/dto";

import $ from "./ProfileSelector.styles";
import type { ProfileSelectorProps } from "./types";

const ProfileSelector: React.FC<ProfileSelectorProps> = ({
  profiles,
  onSelect,
  onCreate,
  onEdit,
}) => {
  const { t } = useTranslation();
  const [activeProfile, setActiveProfile] = useState<ProfileDto | null>(null);

  // react-native's ViewProps has no mouse events — react-native-web adds them at
  // runtime. Spread rather than pass directly, so JSX skips excess-property
  // checking, the same shape UiContainer uses for its hover handlers.
  const hoverProps = (profile: ProfileDto | null) => ({
    onMouseEnter: () => setActiveProfile(profile),
    onMouseLeave: () => setActiveProfile(null),
  });

  return (
    <View style={$.container}>
      {profiles.map((profile, index) => {
        const isEditVisible = activeProfile?.id === profile.id;

        return (
          <Animated.View
            style={$.cell}
            entering={FadeInUp.duration(200).delay(index * 100)}
            key={profile.id}
            {...hoverProps(profile)}
          >
            <ProfileSelectorButton
              name={profile.name}
              tint={profile.tint}
              onPress={() => onSelect(profile)}
              focusable
              testID={TEST_IDS.profile.item(index)}
            />

            {onEdit && (
              <View
                style={$.editAction({ isVisible: isEditVisible })}
                accessibilityElementsHidden={!isEditVisible}
                importantForAccessibility={isEditVisible ? "yes" : "no"}
              >
                <Button
                  name="profile-edit"
                  preset="transparent"
                  size="sm"
                  icon="pencil"
                  isRounded
                  accessibilityLabel={t("PROFILE/OPTIONS/EDIT")}
                  onPress={() => onEdit(profile)}
                  focusable={isEditVisible}
                  testID={
                    isEditVisible ? TEST_IDS.profile.editButton : undefined
                  }
                />
              </View>
            )}
          </Animated.View>
        );
      })}

      {onCreate && profiles.length < MAX_PROFILES && (
        <Animated.View
          style={$.cell}
          entering={FadeInUp.duration(200).delay(profiles.length * 100)}
          {...hoverProps(null)}
        >
          <ProfileSelectorButton
            name=""
            tint={$.createTile.backgroundColor}
            icon="plus"
            hasLightIcon
            accessibilityLabel={t("PROFILE/CREATE/TILE_LABEL")}
            onPress={onCreate}
            focusable
            testID={TEST_IDS.profile.createTile}
          />
        </Animated.View>
      )}
    </View>
  );
};

export default ProfileSelector;
